import type { CardLinkSection } from '@/types/database';

/**
 * Public page block ordering.
 *
 * The public card body is a vertical stack of blocks: the About bio, the
 * Upcoming events list, every typed section, and the catch-all Links block
 * for un-sectioned links. `cards.layout_order` (text[]) optionally pins the
 * order of those blocks so the owner can drag "About" below their gallery,
 * or put Events last.
 *
 * Keys:
 *   'bio'          the About block
 *   'events'       the Upcoming events block
 *   'sec:<id>'     a card_link_sections row
 *   'links'        un-sectioned links
 *
 * Unknown keys are ignored; blocks missing from the order are appended in
 * their default position (bio, events, sections by sort_order, links).
 */

export type BlockKey = 'bio' | 'events' | 'links' | `sec:${string}`;

export const BIO_KEY: BlockKey = 'bio';
export const EVENTS_KEY: BlockKey = 'events';
export const LINKS_KEY: BlockKey = 'links';

export function sectionKey(id: string): BlockKey {
  return `sec:${id}`;
}

export function isSectionKey(key: string): key is `sec:${string}` {
  return key.startsWith('sec:');
}

export function sectionIdFromKey(key: string): string | null {
  return isSectionKey(key) ? key.slice(4) : null;
}

/** Default order: bio, events, sections (by sort_order), then loose links. */
export function defaultBlockOrder(sections: CardLinkSection[]): BlockKey[] {
  const sorted = [...sections].sort((a, b) => a.sort_order - b.sort_order);
  return [BIO_KEY, EVENTS_KEY, ...sorted.map((s) => sectionKey(s.id)), LINKS_KEY];
}

/**
 * Resolve the final order: saved order first (dropping stale keys), then any
 * block the saved order doesn't know about, in default position.
 */
export function resolveBlockOrder(
  saved: string[] | null | undefined,
  sections: CardLinkSection[],
): BlockKey[] {
  const defaults = defaultBlockOrder(sections);
  if (!saved || saved.length === 0) return defaults;
  const known = new Set<string>(defaults);
  const out: BlockKey[] = [];
  for (const k of saved) {
    if (known.has(k) && !out.includes(k as BlockKey)) out.push(k as BlockKey);
  }
  for (const k of defaults) {
    if (!out.includes(k)) {
      // Insert after the nearest preceding default sibling that is present,
      // so a newly added section lands where it would by default rather
      // than always at the very bottom.
      const idx = defaults.indexOf(k);
      let insertAt = out.length;
      for (let i = idx - 1; i >= 0; i--) {
        const pos = out.indexOf(defaults[i]);
        if (pos !== -1) {
          insertAt = pos + 1;
          break;
        }
      }
      // If the previous sibling wasn't found, but a following one is,
      // insert before it.
      if (insertAt === out.length) {
        for (let i = idx + 1; i < defaults.length; i++) {
          const pos = out.indexOf(defaults[i]);
          if (pos !== -1) {
            insertAt = pos;
            break;
          }
        }
      }
      out.splice(insertAt, 0, k);
    }
  }
  return out;
}
